'use client';

import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import { createWarehouse, updateWarehouse, getWarehouseById } from '../../../../stores/warehouseStore';
import { formatUpperCase } from '../../../../utils/formatTitleCase';
import PickerField from '../../../../components/PickerField';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useCompanyDataList } from '../../../../stores/companyStore';

const WarehouseAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    name: '',
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : '',
    warehouse_type: 'warehouse',
    location: '',
    pic_name: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getWarehouseById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            name: data.name ?? prev.name,
            company: data.company_id ?? prev.company,
            warehouse_type: data.warehouse_type ?? prev.warehouse_type,
            location: data.location ?? prev.location,
            pic_name: data.pic_name ?? prev.pic_name
          }));
        }
      } catch (err) {
        console.error('Failed to load warehouse:', err);
        notify({ message: t('warehouse.failedLoadData', 'Failed to load warehouse data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('warehouse.validationNameRequired', 'Warehouse Name is required')),
    company: Yup.string().required(t('warehouse.validationCompanyRequired', 'Company is required')),
    warehouse_type: Yup.string().required(t('warehouse.validationTypeRequired', 'Warehouse Type is required'))
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: formatUpperCase(values.name) ?? '',
          company: Number(values.company),
          warehouse_type: values.warehouse_type,
          location: values.location,
          pic_name: values.pic_name
        };

        if (id) {
          await updateWarehouse(id, payload);
          notify({ message: t('warehouse.updateSuccess', 'Warehouse updated successfully'), severity: 'success' });
        } else {
          await createWarehouse(payload);
          notify({ message: t('warehouse.createSuccess', 'Warehouse added successfully'), severity: 'success' });
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('warehouse.title', 'Warehouse')
          : t('general.add', 'Add') + ' ' + t('warehouse.title', 'Warehouse')
      }
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('masterCompany.title', 'Company') + ' *'}
                  name="company"
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val)}
                  onBlur={() => formik.setFieldTouched('company', true)}
                  error={formik.touched.company && Boolean(formik.errors.company)}
                  helperText={formik.touched.company && formik.errors.company}
                  {...companyPickerParams}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('warehouse.name', 'Warehouse Name') + ' *'}
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    formik.setFieldValue('name', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label={t('size.type', 'Type') + ' *'}
                  name="warehouse_type"
                  value={formik.values.warehouse_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.warehouse_type && Boolean(formik.errors.warehouse_type)}
                  helperText={formik.touched.warehouse_type && formik.errors.warehouse_type}
                >
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="repair-shop">Repair Shop</MenuItem>
                  <MenuItem value="scrap-yard">Scrap Yard</MenuItem>
                  <MenuItem value="retread-vendor">Retread Vendor</MenuItem>
                </TextField>
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('masterCompany.picName', 'PIC Name')}
                  name="pic_name"
                  value={formik.values.pic_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.pic_name && Boolean(formik.errors.pic_name)}
                  helperText={formik.touched.pic_name && formik.errors.pic_name}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('dashboardAsset.location', 'Location')}
                  name="location"
                  multiline
                  rows={3}
                  value={formik.values.location}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.location && Boolean(formik.errors.location)}
                  helperText={formik.touched.location && formik.errors.location}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default WarehouseAddEdit;
